// From signed assessment to "ready to schedule" (brief 1C-3, PLAN.md 5.1 stages 6–7 and
// "Alongside the main flow", 4.8, 4.13, 7.4; fixes after review in brief 1C-4): the care plan
// (services tagged Skilled or Non-skilled, the aide's tasks, precautions; a signed plan never
// changes in place; no title check blocks a plan or a task, a title that doesn't fit is a note),
// aide supervision (the shorter of the manual and the state rule the client type picks),
// authorizations and their usage and watches, physician orders (written or verbal, the company's
// setting, the 30-day clock; the client type says Required, Optional or Not used), what's due
// next, holds and how care resumes, the new episode and recertification (what carries over and
// where it starts, per client type), and when an episode's stage counts as met at once.
// Pure: no database, no clock (the day or the moment is handed in).
//
// No service, title, interval, reason or threshold is named here: those are the company's data
// rows and its home_care setting. Only the shapes of things are.

import { readAuthBillBy, type BillBy } from "../../../rules/billing-rules";
import { weekOf, type WeekSetting } from "../../../rules/week";
import { addDays, daysBetween, refuseSigner } from "./assessments";
import { DATE_REFUSED, dayOf } from "../../../rules/dates";
import { isDay, ruleEffect, type CareRules } from "./patients";
import { EVERY_VISIT, readTaskSchedule, type TaskSchedule } from "./care-tasks";

type Read<T> = { ok: true; value: T } | { ok: false; error: string };
const no = <T>(error: string): Read<T> => ({ ok: false, error });
const obj = (v: unknown): Record<string, unknown> | null =>
  typeof v === "object" && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
const clean = (v: unknown) => (typeof v === "string" ? v.replace(/\s+/g, " ").trim() : "");
const us = (d: string) => `${d.slice(5, 7)}/${d.slice(8, 10)}/${d.slice(0, 4)}`;
const num = (v: number | string | null | undefined) => (v === null || v === undefined ? 0 : +v);
/** A number as it reads: 12, 12.5. */
const amount = (n: number) => String(Math.round(n * 100) / 100);

// ---- The company's setting ------------------------------------------------------------------------

/** The home_care setting (migration 0020 seeds it): verbal orders, clocks, watches, who signs. */
export interface HomeCareSettings {
  /** RN only, or "RN, or LPN with an RN co-sign within N hours" (DECISIONS 10/23). */
  verbalOrders: { lpn: boolean; cosignHours: number };
  /** Every order is signed by the physician within this many days of being taken. */
  orderDays: number;
  orderWarnDays: number;
  authorizationWatch: { usedPct: number; endDays: number };
  /** Board titles that sign a care plan. */
  planSignTitles: string[];
  /** The episode stage a signed, covered plan moves past (to Scheduling). */
  planStage: string;
  /** The aide supervision clock is warned this many days ahead (brief 1D-1). */
  supervisionWarnDays: number;
}

export const HOME_CARE_DEFAULTS: HomeCareSettings = {
  verbalOrders: { lpn: false, cosignHours: 24 },
  orderDays: 30,
  orderWarnDays: 7,
  authorizationWatch: { usedPct: 80, endDays: 14 },
  planSignTitles: ["RN"],
  planStage: "Care plan",
  supervisionWarnDays: 7,
};

const whole = (v: unknown, lo: number, hi: number, d: number) =>
  Number.isInteger(v) && (v as number) >= lo && (v as number) <= hi ? (v as number) : d;

/** The setting read safely from its row: anything missing or wrong takes the default. */
export function homeCareSettings(raw: unknown): HomeCareSettings {
  const d = HOME_CARE_DEFAULTS;
  const b = obj(raw) ?? {};
  const v = obj(b.verbalOrders) ?? {};
  const w = obj(b.authorizationWatch) ?? {};
  const titles = Array.isArray(b.planSignTitles)
    ? b.planSignTitles.filter((t): t is string => typeof t === "string" && t.length > 0)
    : [];
  return {
    verbalOrders: {
      lpn: v.lpn === true,
      cosignHours: whole(v.cosignHours, 1, 72, d.verbalOrders.cosignHours),
    },
    orderDays: whole(b.orderDays, 1, 365, d.orderDays),
    orderWarnDays: whole(b.orderWarnDays, 0, 60, d.orderWarnDays),
    authorizationWatch: {
      usedPct: whole(w.usedPct, 1, 100, d.authorizationWatch.usedPct),
      endDays: whole(w.endDays, 0, 90, d.authorizationWatch.endDays),
    },
    planSignTitles: titles.length ? titles : d.planSignTitles,
    planStage: clean(b.planStage) || d.planStage,
    supervisionWarnDays: whole(b.supervisionWarnDays, 0, 60, d.supervisionWarnDays),
  };
}

/** Home Care › Settings › Verbal orders (company-wide): who takes them, and the co-sign window. */
export function readVerbalOrderSetting(
  input: unknown,
  had: HomeCareSettings,
): Read<HomeCareSettings> {
  const b = obj(input);
  if (!b) return no("Send the verbal order setting.");
  const out: HomeCareSettings = { ...had, verbalOrders: { ...had.verbalOrders } };
  if (b.lpn !== undefined) {
    if (typeof b.lpn !== "boolean") return no("Say whether LPNs take verbal orders.");
    out.verbalOrders.lpn = b.lpn;
  }
  if (b.cosignHours !== undefined) {
    const n = Number(b.cosignHours);
    if (!Number.isInteger(n) || n < 1 || n > 72)
      return no("The RN co-signs within whole hours, 1 to 72.");
    out.verbalOrders.cosignHours = n;
  }
  return { ok: true, value: out };
}

/** The setting in words, as Settings shows it. */
export const verbalOrderWords = (s: HomeCareSettings) =>
  s.verbalOrders.lpn
    ? `RN, or LPN with an RN co-sign within ${s.verbalOrders.cosignHours} hours`
    : "RN only";

// ---- Services, levels and titles (PLAN 4.13) ---------------------------------------------------------

export type Level = "skilled" | "non_skilled";
export type BilledBy = "visit" | "hour" | "trip";
export const LEVEL_LABEL: Record<Level, string> = {
  skilled: "Skilled",
  non_skilled: "Non-skilled",
};
export const BILLED_LABEL: Record<BilledBy, string> = {
  visit: "Visit",
  hour: "Hour",
  trip: "Trip",
};
const UNIT_WORDS: Record<BilledBy, [string, string]> = {
  visit: ["visit", "visits"],
  hour: ["hr", "hrs"],
  trip: ["trip", "trips"],
};
export const unitWords = (u: BilledBy, n: number) => UNIT_WORDS[u][n === 1 ? 0 : 1];

/** A company service as the rules read it (care_services). */
export interface ServiceLike {
  id: string;
  name: string;
  level: Level;
  billed_by: BilledBy;
  titles: readonly string[];
}

/** An aide's service: non-skilled and done by a titled person (a ride has no title). */
export const isAideService = (s: Pick<ServiceLike, "level" | "titles">) =>
  s.level === "non_skilled" && s.titles.length > 0;

/** Which Board titles fit Skilled or Non-skilled work (care_title_levels, data). */
export interface TitleLevelLike {
  title: string;
  level: Level;
}

/**
 * A line takes its service's level (an HHA line can't be Skilled). That's the service's tag, not
 * a title check: no title ever refuses a line (DECISIONS 10/24).
 */
export function refuseLevel(service: Pick<ServiceLike, "name" | "level">, level: Level) {
  return level === service.level
    ? null
    : `${service.name} is ${LEVEL_LABEL[service.level]}: it can't be a ${LEVEL_LABEL[level]} line.`;
}

/**
 * Whether who does it fits the line's level, from the company's title table: a note when it
 * doesn't (an RN on a Non-skilled line, a title on a ride), never a block. Null when it fits or
 * the table doesn't say.
 */
export function titleNote(
  service: Pick<ServiceLike, "name" | "titles">,
  level: Level,
  who: string | null,
  table: readonly TitleLevelLike[],
): string | null {
  if (!who) return null;
  if (!service.titles.length) return `${service.name} isn't usually done by a title.`;
  const fit = table.find((t) => t.title === who);
  if (fit && fit.level !== level)
    return `A ${who} usually does ${LEVEL_LABEL[fit.level].toLowerCase()} work; ${service.name} is ${LEVEL_LABEL[level].toLowerCase()}.`;
  return null;
}

export type LineKind = "service" | "task" | "precaution";
export const LINE_KINDS = ["service", "task", "precaution"] as const;

/** A care plan line as it's added or changed. */
export interface LineInput {
  kind: LineKind;
  serviceId: string | null;
  service: string | null;
  who: string | null;
  level: Level | null;
  billedBy: BilledBy | null;
  authorizationId: string | null;
  what: string;
  howOften: string | null;
  /** A task's schedule (brief 1E-10); null on a service or precaution. */
  schedule: TaskSchedule | null;
}

/**
 * Reads a line: a service (which one, who by title, its level, what and how often, the covering
 * authorization), a task (when in the day and how often, brief 1E-10) or a precaution. Titles are the Board's (credential_types);
 * the level comes from the service unless one is sent, and must be the service's. Which title
 * does it is never refused here (titleNote says when one doesn't fit).
 */
export function readLine(
  input: unknown,
  services: readonly ServiceLike[],
  titles: readonly string[],
  authorizations: readonly { id: string; service_id: string }[],
): Read<LineInput> {
  const b = obj(input);
  if (!b) return no("Send the line.");
  const kind = b.kind;
  if (!(LINE_KINDS as readonly unknown[]).includes(kind)) return no("Say what the line is.");
  const what = clean(b.what);
  const blank: LineInput = {
    kind: kind as LineKind,
    serviceId: null,
    service: null,
    who: null,
    level: null,
    billedBy: null,
    authorizationId: null,
    what,
    howOften: null,
    schedule: null,
  };
  if (kind === "service") {
    const s = services.find((x) => x.id === b.serviceId);
    if (!s) return no("Pick the service.");
    const level =
      b.level === undefined || b.level === null ? s.level : (b.level as Level | undefined);
    if (level !== "skilled" && level !== "non_skilled") return no("Pick Skilled or Non-skilled.");
    const who = clean(b.who) || null;
    if (who && !titles.includes(who)) return no(`${who} isn't a Board title.`);
    const refused = refuseLevel(s, level);
    if (refused) return no(refused);
    if (!what) return no("Say what and how often.");
    if (what.length > 300) return no("What and how often: up to 300 characters.");
    let authorizationId: string | null = null;
    if (b.authorizationId !== undefined && b.authorizationId !== null && b.authorizationId !== "") {
      const a = authorizations.find((x) => x.id === b.authorizationId);
      if (!a) return no("Pick one of this episode's authorizations.");
      if (a.service_id !== s.id) return no(`That authorization isn't for ${s.name}.`);
      authorizationId = a.id;
    }
    return {
      ok: true,
      value: {
        ...blank,
        serviceId: s.id,
        service: s.name,
        who,
        level,
        billedBy: s.billed_by,
        authorizationId,
      },
    };
  }
  if (!what) return no(kind === "task" ? "Say what the task is." : "Say what the precaution is.");
  if (what.length > 300) return no("Up to 300 characters.");
  if (kind === "precaution") return { ok: true, value: blank };
  // how_often stays on old lines; a new task line's label comes from its schedule.
  const schedule = readTaskSchedule(obj(b.schedule) ?? {});
  if (!schedule.ok) return schedule;
  return { ok: true, value: { ...blank, schedule: schedule.value } };
}

/**
 * A signed care plan never changes in place: a changed line is added and the old one archived,
 * with who, when and why. A draft is still being written, so its lines change as they are.
 */
export const lineChange = (planStatus: "draft" | "signed" | "superseded") =>
  planStatus === "draft" ? ("in_place" as const) : ("new_line" as const);

/** Why a line on a signed plan changes or stops (kept with the archived line). */
export function readWhy(input: unknown): Read<string> {
  const why = clean(obj(input)?.why ?? obj(input)?.reason);
  if (!why) return no("Say why the line changed.");
  if (why.length > 300) return no("Up to 300 characters.");
  return { ok: true, value: why };
}

// ---- Signing (only an RN, the company's setting) -----------------------------------------------------

/** Only the titles the company sets sign a care plan (an RN). */
export const refusePlanSigner = (s: HomeCareSettings, title: string | null): string | null =>
  refuseSigner(s.planSignTitles, title);

/**
 * What a plan needs before it's signed: at least one service. No title check blocks signing a
 * plan or a task (DECISIONS 10/24: most aide work is assisting, and many aides are CNA and CMT).
 */
export function refuseSignPlan(lines: readonly Pick<LineInput, "kind">[]): string | null {
  if (!lines.some((l) => l.kind === "service")) return "Add at least one service first.";
  return null;
}

// ---- Drafting from the signed assessment ---------------------------------------------------------------

/** The help levels an ADL answer can have that mean the aide does something. */
const HELP = /some help|full help|needs help/i;

/** What the draft reads from the assessment and the patient. */
export interface DraftInput {
  kind: "skilled" | "consultation" | "mini";
  answers: Record<string, unknown>;
  /** The questions' words by key, for the tasks and the summary. */
  labels: Record<string, string>;
  allergies: string | null;
  codeStatus: string | null;
  activeMedications: number;
  reassessDays: number;
  signedOn: string;
  signedBy: string;
  services: readonly ServiceLike[];
  /**
   * Brief 1E-28: the ADLs answered from the company's list (each with help becomes an aide task,
   * with its note); empty on an assessment from before, which reads its adl.* answers.
   */
  adls?: readonly { item: string; help: "independent" | "some" | "full"; note: string | null }[];
  /** The RN visits the assessment recommends, in days; the RN line takes it. */
  rnEveryDays?: number | null;
}

/**
 * The draft, by rule from the assessment's answers (a person keeps, changes or archives each):
 * a task for each daily-living answer that needs help, medication reminders when the patient
 * takes any, an aide service when there's a task, a nursing service when the assessment was
 * skilled; precautions from fall risk, a fall, the mobility device, allergies and code status;
 * and a written summary.
 */
export function draftFromAssessment(x: DraftInput): {
  lines: LineInput[];
  summary: string;
} {
  const line = (l: Partial<LineInput> & { kind: LineKind; what: string }): LineInput => ({
    serviceId: null,
    service: null,
    who: null,
    level: null,
    billedBy: null,
    authorizationId: null,
    howOften: null,
    schedule: l.kind === "task" ? EVERY_VISIT : null,
    ...l,
  });
  const word = (k: string) => {
    const v = x.answers[k];
    return typeof v === "string" ? v.trim() : typeof v === "number" ? String(v) : "";
  };
  const label = (k: string) => (x.labels[k] ?? k).replace(/^Help needed with /i, "");
  const tasks: LineInput[] = [];
  const needs: string[] = [];
  if (x.adls?.length) {
    for (const a of x.adls) {
      if (a.help === "independent") continue;
      needs.push(`${a.item.toLowerCase()} (${a.help === "full" ? "full help" : "some help"})`);
      tasks.push(
        line({
          kind: "task",
          what: `Help with ${a.item.toLowerCase()}${a.note ? `: ${a.note}` : ""}`,
        }),
      );
    }
  } else
    for (const k of Object.keys(x.answers)
      .filter((k) => k.startsWith("adl."))
      .sort()) {
      const v = word(k);
      if (!HELP.test(v)) continue;
      const what = label(k);
      needs.push(`${what.toLowerCase()} (${v.toLowerCase()})`);
      tasks.push(line({ kind: "task", what: `Help with ${what.toLowerCase()}` }));
    }
  if (x.activeMedications > 0 && !tasks.some((t) => /^Help with medications/.test(t.what)))
    tasks.push(line({ kind: "task", what: "Medication reminders" }));
  const services: LineInput[] = [];
  const aide = x.services.find(isAideService);
  if (tasks.length && aide)
    services.push(
      line({
        kind: "service",
        serviceId: aide.id,
        service: aide.name,
        who: aide.titles[aide.titles.length > 1 ? 1 : 0] ?? null,
        level: aide.level,
        billedBy: aide.billed_by,
        what: "Hours and days as agreed with the patient",
      }),
    );
  const nurse = x.services.find((s) => s.level === "skilled" && s.titles.length > 0);
  if (x.kind === "skilled" && nurse)
    services.push(
      line({
        kind: "service",
        serviceId: nurse.id,
        service: nurse.name,
        who: nurse.titles[0] ?? null,
        level: nurse.level,
        billedBy: nurse.billed_by,
        what: x.rnEveryDays
          ? `RN visit every ${x.rnEveryDays} days, PRN as ordered`
          : `Reassessment every ${x.reassessDays} days, PRN as ordered`,
      }),
    );
  const precautions: LineInput[] = [];
  const fall = word("home.fallrisk");
  if (/high|moderate/i.test(fall))
    precautions.push(line({ kind: "precaution", what: `Fall risk: ${fall}` }));
  if (/^(yes|true)$/i.test(word("fall.risk")) || x.answers["fall.risk"] === true)
    precautions.push(line({ kind: "precaution", what: "Fell in the last 90 days" }));
  const device = word("mob.device");
  if (device && !/^(none|no)$/i.test(device))
    precautions.push(line({ kind: "precaution", what: `Mobility device: ${device}` }));
  if (x.allergies?.trim())
    precautions.push(line({ kind: "precaution", what: `Allergies: ${x.allergies.trim()}` }));
  if (x.codeStatus && !/full code|not discussed/i.test(x.codeStatus))
    precautions.push(line({ kind: "precaution", what: `Code status: ${x.codeStatus}` }));

  const parts = [`From the assessment signed ${us(x.signedOn)} by ${x.signedBy}.`];
  if (needs.length) parts.push(`Needs help with ${needs.join(", ")}.`);
  if (fall) parts.push(`Fall risk: ${fall.toLowerCase()}.`);
  if (x.activeMedications)
    parts.push(
      `${x.activeMedications} medication${x.activeMedications === 1 ? "" : "s"} on the list.`,
    );
  const rec = word("asm.recommend");
  if (rec) parts.push(`Recommendations: ${rec}`);
  return {
    lines: [...services, ...tasks, ...precautions].map((l) => ({
      ...l,
      what: l.what.slice(0, 300),
    })),
    summary: parts.join(" ").slice(0, 4000),
  };
}

/** A plan's dates when drafted: from the start of care (or today) for the client type's days. */
export function planDates(startOfCare: string | null, today: string, days: number) {
  const from = startOfCare ?? today;
  return { startsOn: from, endsOn: addDays(from, Math.max(days, 1) - 1) };
}

export function readPlanDates(input: unknown): Read<{ startsOn: string; endsOn: string }> {
  const b = obj(input);
  if (!b || !isDay(b.startsOn) || !isDay(b.endsOn)) return no("Give the plan's from and to dates.");
  if (b.endsOn < b.startsOn) return no("The plan ends after it starts.");
  return { ok: true, value: { startsOn: b.startsOn, endsOn: b.endsOn } };
}

/** "60 days" for a plan's length, as its card's eyebrow says. */
export const planLength = (startsOn: string, endsOn: string) =>
  `${daysBetween(startsOn, endsOn) + 1} days`;

// ---- Aide supervision (COMAR 10.07.05.12E, manual PDP 37) --------------------------------------------

export interface SupervisionRuleLike {
  kind: "state" | "manual";
  source: string;
  applies: "gives_medications" | "assists_medications" | "otherwise";
  every_days: number;
}

export type SupervisionApplies = SupervisionRuleLike["applies"];
export const SUPERVISION_APPLIES = [
  "gives_medications",
  "assists_medications",
  "otherwise",
] as const;
const APPLIES_WORDS: Record<SupervisionApplies, string> = {
  gives_medications: "the aide gives medications",
  assists_medications: "the aide assists with medications",
  otherwise: "no medications",
};
/** The client type's choice, as Settings says it. */
export const SUPERVISION_LABEL: Record<SupervisionApplies, string> = {
  gives_medications: "Aides give medications",
  assists_medications: "Aides assist with medications",
  otherwise: "No medications",
};

/**
 * How often an RN supervises the aide: when the plan has an aide, the state rule the client type
 * picks (its aide supervision setting) and the company's manual setting for the same, and the
 * shorter of the two always wins. Never read from a task's words or a title. Null when the plan
 * has no aide.
 */
export function supervisionClock(
  lines: readonly { kind: LineKind; level: Level | null; who: string | null }[],
  rules: readonly SupervisionRuleLike[],
  applies: SupervisionApplies,
): { everyDays: number; words: string; why: string } | null {
  if (!lines.some((l) => l.kind === "service" && l.level === "non_skilled" && l.who)) return null;
  const state = rules.find((r) => r.kind === "state" && r.applies === applies);
  const manual = rules.find((r) => r.kind === "manual" && r.applies === applies);
  const both = [state, manual].filter((r): r is SupervisionRuleLike => !!r);
  if (!both.length) return null;
  const wins = both.reduce((a, b) => (b.every_days < a.every_days ? b : a));
  const other = both.find((r) => r !== wins);
  const why =
    other && other.every_days !== wins.every_days
      ? `${wins.source} (every ${wins.every_days} days) is stricter than ${other.source} (every ${other.every_days} days) when ${APPLIES_WORDS[applies]}`
      : `${wins.source} when ${APPLIES_WORDS[applies]}`;
  return { everyDays: wins.every_days, words: `every ${wins.every_days} days`, why };
}

// ---- Authorizations (PLAN 7.4) --------------------------------------------------------------------------

export type AuthKind = "count" | "frequency" | "continuous";
export const AUTH_KINDS = ["count", "frequency", "continuous"] as const;
export const AUTH_KIND_LABEL: Record<AuthKind, string> = {
  count: "Count",
  frequency: "Frequency",
  continuous: "Continuous coverage",
};

export interface AuthInput {
  number: string;
  serviceId: string;
  kind: AuthKind;
  unit: BilledBy;
  total: number | null;
  perPeriod: number | null;
  period: "week" | "month" | null;
  /** With period week: 2 to 52 weeks in each of the authorization's own periods; null is each week. */
  periodWeeks: number | null;
  startsOn: string;
  endsOn: string;
  letterFileId: string | null;
  overagePrivatePay: boolean;
  /** Brief 1E-35: its own Bill by (null: the contract's). */
  billBy: BillBy | null;
}

const amountOf = (v: unknown) => {
  const n = typeof v === "string" ? Number(v.trim()) : typeof v === "number" ? v : NaN;
  return Number.isFinite(n) && n > 0 && n < 100000 ? Math.round(n * 100) / 100 : null;
};

/** Reads an authorization: number, service, shape (count, frequency or continuous), dates. */
export function readAuthorization(
  input: unknown,
  services: readonly ServiceLike[],
  fileIds: readonly string[],
): Read<AuthInput> {
  const b = obj(input);
  if (!b) return no("Send the authorization.");
  const number = clean(b.number);
  if (!number) return no("Give the authorization or certificate number.");
  if (number.length > 60) return no("The number is up to 60 characters.");
  const s = services.find((x) => x.id === b.serviceId);
  if (!s) return no("Pick the service it covers.");
  if (!(AUTH_KINDS as readonly unknown[]).includes(b.kind))
    return no("Pick count, frequency or continuous coverage.");
  const kind = b.kind as AuthKind;
  if (!b.startsOn || !b.endsOn) return no("Give the dates it's valid.");
  // One date rule (brief 1E-23): MM/DD/YYYY or a date picker's day, a real day, or refused.
  const startsOn = dayOf(b.startsOn);
  const endsOn = dayOf(b.endsOn);
  if (!startsOn || !endsOn) return no(DATE_REFUSED);
  if (endsOn < startsOn) return no("It ends after it starts.");
  let total: number | null = null;
  let perPeriod: number | null = null;
  let period: "week" | "month" | null = null;
  let periodWeeks: number | null = null;
  if (kind === "count") {
    total = amountOf(b.total);
    if (total === null) return no(`How many ${unitWords(s.billed_by, 2)} does it allow in all?`);
  }
  if (kind === "frequency") {
    perPeriod = amountOf(b.perPeriod);
    if (perPeriod === null) return no(`How many ${unitWords(s.billed_by, 2)} each period?`);
    if (!(AUTH_PERIODS as readonly unknown[]).includes(b.period)) return no("Pick the period.");
    period = b.period === "month" ? "month" : "week";
    if (b.period === "two_weeks") periodWeeks = 2;
    if (b.period === "weeks") {
      const n = typeof b.periodWeeks === "string" ? Number(b.periodWeeks.trim()) : b.periodWeeks;
      if (typeof n !== "number" || !Number.isInteger(n) || n < 2 || n > 52)
        return no("Every n weeks is 2 to 52 weeks.");
      periodWeeks = n;
    }
    total = b.total === undefined || b.total === null || b.total === "" ? null : amountOf(b.total);
  }
  let letterFileId: string | null = null;
  if (b.letterFileId) {
    if (typeof b.letterFileId !== "string" || !fileIds.includes(b.letterFileId))
      return no("The letter must be one of this patient's files.");
    letterFileId = b.letterFileId;
  }
  const billBy = readAuthBillBy(b.billBy);
  if (!billBy.ok) return no(billBy.error);
  return {
    ok: true,
    value: {
      billBy: billBy.value,
      number,
      serviceId: s.id,
      kind,
      unit: s.billed_by,
      total,
      perPeriod,
      period,
      periodWeeks,
      startsOn,
      endsOn,
      letterFileId,
      overagePrivatePay: b.overagePrivatePay === true,
    },
  };
}

export interface AuthLike {
  id: string;
  service_id: string;
  kind: AuthKind;
  unit: BilledBy;
  total: number | string | null;
  per_period: number | string | null;
  period: "week" | "month" | null;
  /** 2 to 52 weeks in each period, counted from starts_on; null is each (company) week. */
  period_weeks?: number | null;
  starts_on: string;
  ends_on: string;
  archived_at: Date | null;
}
export interface UsageLike {
  used_on: string;
  used: number | string;
  voided_at: Date | null;
}

/** The periods a frequency is counted in (the mockup's Period list). */
export const AUTH_PERIODS = ["week", "two_weeks", "weeks", "month"] as const;
export type AuthPeriod = (typeof AUTH_PERIODS)[number];
export const AUTH_PERIOD_LABEL: Record<AuthPeriod, string> = {
  week: "Each week",
  two_weeks: "Every two weeks",
  weeks: "Every n weeks",
  month: "Each month",
};

/** Which of the Period list an authorization is on. */
export function authPeriodOf(a: Pick<AuthLike, "period" | "period_weeks">): AuthPeriod {
  if (a.period === "month") return "month";
  if (!a.period_weeks) return "week";
  return a.period_weeks === 2 ? "two_weeks" : "weeks";
}

/** The period in words: "week", "month", "2 weeks". */
export const periodWords = (a: Pick<AuthLike, "period" | "period_weeks">) =>
  a.period === "week" && a.period_weeks ? `${a.period_weeks} weeks` : (a.period ?? "");

/** "this week", "this month", "these 2 weeks". */
export const thisPeriod = (a: Pick<AuthLike, "period" | "period_weeks">) =>
  a.period === "week" && a.period_weeks ? `these ${a.period_weeks} weeks` : `this ${a.period}`;

/** The authorization's shape in words: "40 visits", "20 hrs/week", "6 visits/2 weeks". */
export function authShape(
  a: Pick<AuthLike, "kind" | "unit" | "total" | "per_period" | "period" | "period_weeks">,
) {
  if (a.kind === "continuous") return AUTH_KIND_LABEL.continuous;
  if (a.kind === "count") return `${amount(num(a.total))} ${unitWords(a.unit, num(a.total))}`;
  return `${amount(num(a.per_period))} ${unitWords(a.unit, num(a.per_period))}/${periodWords(a)}`;
}

/** A week starts on the company's day (brief 1D-5, server/rules/week.ts); a month on its 1st. */
export function periodStart(day: string, period: "week" | "month", week: WeekSetting): string {
  if (period === "month") return `${day.slice(0, 8)}01`;
  return weekOf(day, week).start;
}
const periodEnd = (start: string, period: "week" | "month", week: WeekSetting) => {
  if (period === "week") return weekOf(start, week).end;
  const y = +start.slice(0, 4);
  const m = +start.slice(5, 7);
  return addDays(`${m === 12 ? y + 1 : y}-${String(m === 12 ? 1 : m + 1).padStart(2, "0")}-01`, -1);
};

/**
 * The period of a frequency authorization that holds a day. Each week is the company's workweek
 * and each month the calendar month. Every two weeks and every n weeks are counted in the
 * authorization's own periods: the first starts on its From date, each lasts n weeks.
 */
export function authPeriod(
  a: Pick<AuthLike, "period" | "period_weeks" | "starts_on">,
  day: string,
  week: WeekSetting,
): { start: string; end: string } {
  const period = a.period ?? "week";
  if (period === "week" && a.period_weeks) {
    const len = 7 * a.period_weeks;
    const start = addDays(a.starts_on, len * Math.floor(daysBetween(a.starts_on, day) / len));
    return { start, end: addDays(start, len - 1) };
  }
  const start = periodStart(day, period, week);
  return { start, end: periodEnd(start, period, week) };
}

/**
 * Units used, derived from the usage rows (never stored by hand): a count against its total, a
 * frequency in the period that holds today (or the last one it ran), continuous coverage by its
 * dates. Low balance at the watch's %; ending within the watch's days of the end.
 */
export function authUsage(
  a: AuthLike,
  usage: readonly UsageLike[],
  today: string,
  watch: HomeCareSettings["authorizationWatch"],
  week: WeekSetting,
) {
  const live = usage.filter((u) => !u.voided_at);
  let used: number;
  let of: number | null = null;
  let period: { start: string; end: string } | null = null;
  if (a.kind === "count") {
    used = live.reduce((t, u) => t + num(u.used), 0);
    of = num(a.total);
  } else if (a.kind === "frequency" && a.period) {
    const at = today < a.starts_on ? a.starts_on : today > a.ends_on ? a.ends_on : today;
    period = authPeriod(a, at, week);
    const { start, end } = period;
    used = live
      .filter((u) => u.used_on >= start && u.used_on <= end)
      .reduce((t, u) => t + num(u.used), 0);
    of = num(a.per_period);
  } else used = live.reduce((t, u) => t + num(u.used), 0);
  const pct = of ? Math.round((100 * used) / of) : null;
  const endsIn = daysBetween(today, a.ends_on);
  const started = today >= a.starts_on;
  return {
    used: Math.round(used * 100) / 100,
    of,
    pct,
    period,
    low: pct !== null && pct >= watch.usedPct,
    over: of !== null && used > of,
    endsIn,
    ending: endsIn >= 0 && endsIn <= watch.endDays,
    ended: endsIn < 0,
    started,
    words:
      of !== null
        ? `${amount(used)} of ${amount(of)} ${unitWords(a.unit, of)} used${period ? ` ${thisPeriod(a)}` : ""}`
        : `${amount(used)} ${unitWords(a.unit, used)} used`,
  };
}

/** "Due in 9 days", "Ends today", "Ended 10/21/2026" (the mockup's dueChip). */
export function dueWords(
  endsOn: string,
  today: string,
): { words: string; level: "ok" | "warn" | "bad" } {
  const left = daysBetween(today, endsOn);
  if (left < 0) return { words: `Ended ${us(endsOn)}`, level: "bad" };
  if (left === 0) return { words: "Ends today", level: "bad" };
  return { words: `Due in ${left} day${left === 1 ? "" : "s"}`, level: left <= 14 ? "warn" : "ok" };
}

/**
 * The watches an authorization has raised by now: once at the watch's % used (once per period for
 * a frequency), once within the watch's days of the end. Each key is raised once; the watch never
 * raises a key it already has.
 */
export function authWatches(
  a: Pick<AuthLike, "id" | "kind" | "archived_at">,
  u: Pick<ReturnType<typeof authUsage>, "low" | "ending" | "period" | "pct" | "endsIn">,
  watch: HomeCareSettings["authorizationWatch"],
): { key: string; kind: "used" | "end"; words: string }[] {
  if (a.archived_at) return [];
  const out: { key: string; kind: "used" | "end"; words: string }[] = [];
  if (u.low)
    out.push({
      key: `auth_used:${a.id}${u.period ? `:${u.period.start}` : ""}`,
      kind: "used",
      words: `${u.pct}% used (watch at ${watch.usedPct}%)`,
    });
  if (u.ending)
    out.push({
      key: `auth_end:${a.id}`,
      kind: "end",
      words: u.endsIn === 0 ? "Ends today" : `Ends in ${u.endsIn} day${u.endsIn === 1 ? "" : "s"}`,
    });
  return out;
}

export function readUsage(
  input: unknown,
  today: string,
): Read<{ usedOn: string; used: number; note: string | null }> {
  const b = obj(input);
  if (!b) return no("Send the units used.");
  const used = amountOf(b.used);
  if (used === null) return no("How many were used?");
  const usedOn = b.usedOn === undefined || b.usedOn === "" ? today : b.usedOn;
  if (!isDay(usedOn)) return no("When were they used?");
  if (usedOn > today) return no("Units are entered once they're used.");
  const note = clean(b.note);
  if (note.length > 300) return no("The note is up to 300 characters.");
  return { ok: true, value: { usedOn, used, note: note || null } };
}

/** The flag on typed usage a counted visit may already cover (brief 1E-15). */
export const DOUBLE_COUNT_WORDS = "Possible double count: a visit on this day is already counted";

/**
 * Typed usage that may count a visit twice (brief 1E-15): since 1E-13 each validated visit counts
 * against its authorization, so usage typed in by hand for a day that already has a counted visit
 * on the same authorization is flagged, until it's kept as it is or archived with a reason. Typed
 * usage on a day with no counted visit (work done before FlowCare) is never flagged.
 */
export function possibleDoubleCounts<
  U extends {
    authorization_id: string;
    used_on: string;
    source: string;
    voided_at: Date | null;
    kept_at: Date | null;
  },
>(typed: readonly U[], counted: readonly { authorization_id: string; used_on: string }[]): U[] {
  const days = new Set(counted.map((c) => `${c.authorization_id}|${c.used_on}`));
  return typed.filter(
    (u) =>
      u.source === "entered" &&
      !u.voided_at &&
      !u.kept_at &&
      days.has(`${u.authorization_id}|${u.used_on}`),
  );
}

/** An authorization covers a service line when it's for that service and live (not archived or ended). */
export const covers = (
  a: Pick<AuthLike, "service_id" | "ends_on" | "archived_at">,
  serviceId: string | null,
  today: string,
) => !!serviceId && a.service_id === serviceId && !a.archived_at && a.ends_on >= today;

/**
 * Whether the plan's services are covered: each live service line points at an authorization that
 * covers it. The client's rules say whether one is needed (authorizations needed and the "No
 * authorization" rule not Off). Missing ones are only flagged for care; at Block, billing is held.
 */
export function planCoverage(
  lines: readonly {
    kind: LineKind;
    service: string | null;
    service_id: string | null;
    authorization_id: string | null;
  }[],
  auths: readonly Pick<AuthLike, "id" | "service_id" | "ends_on" | "archived_at">[],
  rules: CareRules,
  today: string,
) {
  const needed = rules.authorization.needed && rules.money.no_authorization !== "off";
  const uncovered = lines
    .filter((l) => l.kind === "service")
    .filter((l) => {
      const a = auths.find((x) => x.id === l.authorization_id);
      return !a || !covers(a, l.service_id, today);
    })
    .map((l) => l.service ?? "A service");
  const missing = needed ? [...new Set(uncovered)] : [];
  const effect = ruleEffect(rules, "no_authorization");
  return {
    needed,
    missing,
    covered: missing.length === 0,
    level: missing.length ? rules.money.no_authorization : ("off" as const),
    care: missing.length ? effect.care : { allowed: true, flagged: false, held: false },
    billing: missing.length ? effect.money : { allowed: true, flagged: false, held: false },
  };
}

export const NO_AUTH_FLAG = "no_authorization";
export const OVER_UNITS_FLAG = "over_units";

// ---- Physician orders (DECISIONS 10/23 verbal orders) ---------------------------------------------------

export type OrderKind = "written" | "verbal";
export const ORDER_KIND_LABEL: Record<OrderKind, string> = { written: "Written", verbal: "Verbal" };

/** A client type's Physician orders setting (DECISIONS 10/24). */
export type OrdersUse = "required" | "optional" | "not_used";
export const ORDERS_USES = ["required", "optional", "not_used"] as const;
export const ORDERS_USE_LABEL: Record<OrdersUse, string> = {
  required: "Required",
  optional: "Optional",
  not_used: "Not used",
};
export const ordersUseOf = (v: unknown): OrdersUse =>
  (ORDERS_USES as readonly unknown[]).includes(v) ? (v as OrdersUse) : "not_used";

/** The Next steps card (orders, what's due, appointments) shows when the type uses orders. */
export const showsNextSteps = (use: OrdersUse) => use !== "not_used";

/** Orders are taken and filed only for a type that uses them. */
export const refuseOrders = (use: OrdersUse) =>
  use === "not_used"
    ? "This client type doesn't use physician orders (Settings › Home care client types)."
    : null;

/**
 * With physician orders Required, a signed plan waits for one of the episode's orders to be
 * signed by the physician. That's a flag only (brief 1D-1): the plan still moves the episode on to
 * Scheduling at once, and visits are scheduled; the flag stays until an order is signed back.
 */
export const waitsForOrder = (use: OrdersUse, orders: readonly { signed_on: string | null }[]) =>
  use === "required" && !orders.some((o) => !!o.signed_on);

export const ORDER_NEEDED_FLAG = "order_needed";

export function readOrder(
  input: unknown,
  fileIds: readonly string[],
): Read<{ kind: OrderKind; what: string; fromWhom: string; fileId: string | null }> {
  const b = obj(input);
  if (!b) return no("Send the order.");
  if (b.kind !== "written" && b.kind !== "verbal") return no("Written or verbal?");
  const what = typeof b.what === "string" ? b.what.trim() : "";
  if (!what) return no("Say what the physician ordered.");
  if (what.length > 2000) return no("The order is up to 2,000 characters.");
  const fromWhom = clean(b.fromWhom);
  if (!fromWhom) return no("Say which physician it's from.");
  if (fromWhom.length > 160) return no("Up to 160 characters.");
  let fileId: string | null = null;
  if (b.kind === "written") {
    if (typeof b.fileId !== "string" || !fileIds.includes(b.fileId))
      return no("A written order is filed with its page: pick one of this patient's files.");
    fileId = b.fileId;
  }
  return { ok: true, value: { kind: b.kind, what, fromWhom, fileId } };
}

/**
 * A verbal order follows the company setting: an RN takes it; an LPN only when the company allows
 * LPNs, and then an RN co-signs it within the setting's hours (it's flagged until then).
 */
export function verbalOrderRule(
  s: HomeCareSettings,
  title: string | null,
): { ok: true; cosign: boolean } | { ok: false; error: string } {
  if (title === "RN") return { ok: true, cosign: false };
  if (title === "LPN" && s.verbalOrders.lpn) return { ok: true, cosign: true };
  if (title === "LPN")
    return {
      ok: false,
      error:
        "Verbal orders are taken by an RN here. An Admin can allow LPNs, with an RN co-sign, in Home Care › Settings.",
    };
  return {
    ok: false,
    error: `Only ${s.verbalOrders.lpn ? "an RN or an LPN" : "an RN"} takes a verbal order. ${title ? `The professional title on this profile is ${title}.` : "This profile has no nursing title."}`,
  };
}

/** Only an RN co-signs an LPN's verbal order. */
export const refuseCosigner = (title: string | null) =>
  title === "RN" ? null : "Only an RN co-signs a verbal order.";

export const cosignDue = (takenAt: Date, hours: number) =>
  new Date(takenAt.getTime() + hours * 3_600_000);

/** An LPN's verbal order waiting for its RN co-sign: waiting, late after the deadline, or done. */
export function cosignState(
  o: { cosign_due_at: Date | null; cosigned_at: Date | null },
  now: Date,
): "none" | "waiting" | "late" | "done" {
  if (!o.cosign_due_at) return "none";
  if (o.cosigned_at) return "done";
  return now.getTime() > o.cosign_due_at.getTime() ? "late" : "waiting";
}

/**
 * The 30-day clock (PDC 41): every order is signed by the physician within the setting's days of
 * being taken, warned the setting's days ahead and flagged when unsigned after.
 */
export function orderClock(
  o: { taken_at: Date; signed_on: string | null },
  now: Date,
  s: Pick<HomeCareSettings, "orderDays" | "orderWarnDays">,
): { due: Date; state: "signed" | "ok" | "warn" | "overdue"; daysLeft: number } {
  const due = new Date(o.taken_at.getTime() + s.orderDays * 86_400_000);
  const daysLeft = Math.ceil((due.getTime() - now.getTime()) / 86_400_000);
  if (o.signed_on) return { due, state: "signed", daysLeft };
  if (now.getTime() > due.getTime()) return { due, state: "overdue", daysLeft };
  return { due, state: daysLeft <= s.orderWarnDays ? "warn" : "ok", daysLeft };
}

export const cosignFlagKey = (orderId: string) => `order_cosign:${orderId}`;
export const unsignedFlagKey = (orderId: string) => `order_unsigned:${orderId}`;
export const orderWarnKey = (orderId: string) => `order_warn:${orderId}`;

export function readSigned(
  input: unknown,
  fileIds: readonly string[],
  today: string,
): Read<{ signedOn: string; fileId: string }> {
  const b = obj(input);
  if (!b) return no("Send the signed copy.");
  if (typeof b.fileId !== "string" || !fileIds.includes(b.fileId))
    return no("Pick the signed copy: one of this patient's files.");
  const signedOn = b.signedOn === undefined || b.signedOn === "" ? today : b.signedOn;
  if (!isDay(signedOn) || signedOn > today) return no("When did the physician sign it?");
  return { ok: true, value: { signedOn, fileId: b.fileId } };
}

// ---- Holds and how care resumes (PLAN 5.1 "Hospital stays") ---------------------------------------------

export function readHold(
  input: unknown,
  reasons: readonly { value: string; hospital: boolean }[],
  services: readonly string[],
  today: string,
): Read<{ reason: string; hospital: boolean; startsOn: string; paused: string[] }> {
  const b = obj(input);
  if (!b) return no("Send the hold.");
  const r = reasons.find((x) => x.value === b.reason);
  if (!r) return no("Pick why care is on hold.");
  const startsOn = b.startsOn === undefined || b.startsOn === "" ? today : b.startsOn;
  if (!isDay(startsOn)) return no("Give the day it starts.");
  const paused = Array.isArray(b.paused)
    ? b.paused.filter((s): s is string => typeof s === "string")
    : [];
  const unknown = paused.find((s) => !services.includes(s));
  if (unknown) return no(`${unknown} isn't on the care plan.`);
  return {
    ok: true,
    value: { reason: r.value, hospital: r.hospital, startsOn, paused: [...new Set(paused)] },
  };
}

export type HoldEnd = "resume" | "resumption" | "new_episode";
export const HOLD_ENDS = ["resume", "resumption", "new_episode"] as const;
/** Coming home: the three choices, as the mockup's resume card says them. */
export const HOLD_END_LABEL: Record<HoldEnd, { label: string; sub: string }> = {
  resume: {
    label: "Resume",
    sub: "Short stay, nothing changed. Same episode and plan; visits go back on the schedule.",
  },
  resumption: {
    label: "Resumption of care",
    sub: "An RN visit first: new medications, what changed. Same episode; a new assessment is due and the plan is updated after.",
  },
  new_episode: {
    label: "New episode",
    sub: "A real change. Starts over: new assessment, new care plan. This episode closes.",
  },
};

export function readResume(
  input: unknown,
  holdStart: string,
  today: string,
): Read<{ how: HoldEnd; backOn: string }> {
  const b = obj(input);
  if (!b) return no("Say how the patient is coming back.");
  if (!(HOLD_ENDS as readonly unknown[]).includes(b.how)) return no("Pick how care resumes.");
  const backOn = b.backOn === undefined || b.backOn === "" ? today : b.backOn;
  if (!isDay(backOn)) return no("Give the day they're back home.");
  if (backOn < holdStart) return no("They come back after the hold started.");
  return { ok: true, value: { how: b.how as HoldEnd, backOn } };
}

/**
 * The days a hospital stay paused the reassessment clock since a day: each hospital hold's days
 * from then (an open one up to today), and whether one is open now (the clock is paused).
 */
export function heldDays(
  holds: readonly { hospital: boolean; starts_on: string; ends_on: string | null }[],
  since: string | null,
  today: string,
): { days: number; since: string | null } {
  let days = 0;
  let open: string | null = null;
  for (const h of holds.filter((x) => x.hospital)) {
    const from = since && h.starts_on < since ? since : h.starts_on;
    const to = h.ends_on ?? today;
    if (!h.ends_on) open = h.starts_on;
    if (to > from) days += daysBetween(from, to);
  }
  return { days, since: open };
}

// ---- The new episode and recertification (per client type, DECISIONS 10/24) ---------------------------

export type Carry = "payer" | "consents" | "authorizations" | "care_team";
export const CARRIES = ["payer", "consents", "authorizations", "care_team"] as const;
export const CARRY_LABEL: Record<Carry, string> = {
  payer: "Who pays",
  consents: "Consents",
  authorizations: "Authorizations",
  care_team: "Care team",
};

/** What carries over to a new episode or recertification, and where the new one starts. */
export interface FollowOn {
  /** Recertification is used by this client type. */
  recertification: boolean;
  carries: Carry[];
  /** A stage of the type's workflow; null: where a new assessment is filled. */
  startsAt: string | null;
}

/** A type with nothing set yet: no recertification; who pays and consents carry over. */
export const FOLLOW_ON_DEFAULTS: FollowOn = {
  recertification: false,
  carries: ["payer", "consents"],
  startsAt: null,
};

/** The type's follow_on read safely: anything missing or wrong takes the default. */
export function followOnOf(raw: unknown): FollowOn {
  const b = obj(raw);
  if (!b || !Object.keys(b).length) return { ...FOLLOW_ON_DEFAULTS };
  const carries = Array.isArray(b.carries)
    ? CARRIES.filter((c) => (b.carries as unknown[]).includes(c))
    : FOLLOW_ON_DEFAULTS.carries;
  return {
    recertification: b.recertification === true,
    carries,
    startsAt: clean(b.startsAt) || null,
  };
}

/** Settings sends the choice: recertification used or not, what carries over, where it starts. */
export function readFollowOn(input: unknown, stages: readonly string[]): Read<FollowOn> {
  const b = obj(input);
  if (!b) return no("Say what carries over to a new episode.");
  if (typeof b.recertification !== "boolean") return no("Say whether recertification is used.");
  if (
    !Array.isArray(b.carries) ||
    b.carries.some((c) => !(CARRIES as readonly unknown[]).includes(c))
  )
    return no("Pick what carries over: who pays, consents, authorizations, care team.");
  const startsAt = clean(b.startsAt) || null;
  if (startsAt && !stages.includes(startsAt))
    return no("Pick the stage the new episode starts at, from the type's workflow.");
  return {
    ok: true,
    value: {
      recertification: b.recertification,
      carries: CARRIES.filter((c) => (b.carries as unknown[]).includes(c)),
      startsAt,
    },
  };
}

/** "Who pays, consents" for what carries over, "Nothing" when none. */
export const carryWords = (f: FollowOn) =>
  f.carries.length
    ? f.carries.map((c, i) => (i ? CARRY_LABEL[c].toLowerCase() : CARRY_LABEL[c])).join(", ")
    : "Nothing";

/**
 * Where the new episode starts: the type's stage when it's on the workflow, else the stage a new
 * assessment fills (1C-3), else the first.
 */
export function followStart(
  f: Pick<FollowOn, "startsAt">,
  stages: readonly string[],
  assessmentStages: readonly string[],
): number {
  const own = f.startsAt ? stages.indexOf(f.startsAt) : -1;
  if (own >= 0) return own;
  return Math.max(
    0,
    stages.findIndex((s) => assessmentStages.includes(s)),
  );
}

// ---- What's due next (the Next steps card) -------------------------------------------------------------

export interface DueItem {
  what: string;
  words: string;
  level: "ok" | "warn" | "bad" | "plain";
}

/**
 * The Next steps card's Due next, in the mockup's order: the reassessment, the recertification,
 * each unsigned order with its days left on the clock (or the order a Required type still needs),
 * and each authorization ending within the watch's days.
 */
export function dueNext(x: {
  reassessment: { words: string; state: string } | null;
  recertification: { used: boolean; endsOn: string | null; signed: boolean };
  orders: readonly {
    kindLabel: string;
    taken: string;
    signed: boolean;
    daysLeft: number;
    state: string;
  }[];
  orderNeeded: boolean;
  orderDays: number;
  authorizations: readonly { service: string; number: string; ending: boolean; due: string }[];
}): DueItem[] {
  const out: DueItem[] = [];
  if (x.reassessment)
    out.push({
      what: "Reassessment",
      words: x.reassessment.words,
      level:
        x.reassessment.state === "overdue"
          ? "bad"
          : x.reassessment.state === "warn" || x.reassessment.state === "paused"
            ? "warn"
            : "plain",
    });
  out.push({
    what: "Recertification",
    words: !x.recertification.used
      ? "Not used by this client"
      : x.recertification.endsOn && x.recertification.signed
        ? `Before the plan ends ${us(x.recertification.endsOn)}`
        : "When the care plan is signed",
    level: "plain",
  });
  for (const o of x.orders.filter((y) => !y.signed))
    out.push({
      what: "Order signature",
      words:
        o.state === "overdue"
          ? `${o.kindLabel} order ${o.taken} · unsigned after ${x.orderDays} days`
          : `${o.kindLabel} order ${o.taken} · ${o.daysLeft} day${o.daysLeft === 1 ? "" : "s"} left`,
      level: o.state === "overdue" ? "bad" : "warn",
    });
  if (x.orderNeeded && !x.orders.some((o) => !o.signed))
    out.push({
      what: "Order signature",
      words: "A signed physician order is needed",
      level: "warn",
    });
  for (const a of x.authorizations.filter((y) => y.ending))
    out.push({
      what: `Authorization ${a.number}`,
      words: `${a.service} · ${a.due}`,
      level: "warn",
    });
  return out;
}

// ---- Recertification ---------------------------------------------------------------------------------

/**
 * A recertification needs the client type to use it, and a signed plan on an episode that isn't
 * closed; it starts the day after.
 */
export function refuseRecertify(
  plan: { status: string; ends_on: string } | null,
  episode: { status: string } | null,
  follow: Pick<FollowOn, "recertification"> = { recertification: true },
): string | null {
  if (!follow.recertification) return "This client type doesn't use recertification.";
  if (!episode || episode.status === "closed") return "There's no open episode to recertify.";
  if (!plan || plan.status !== "signed") return "Recertify a signed care plan.";
  return null;
}

export function readRecertify(
  input: unknown,
  plan: { ends_on: string; starts_on: string },
): Read<{ startsOn: string }> {
  const b = obj(input) ?? {};
  const startsOn =
    b.startsOn === undefined || b.startsOn === "" ? addDays(plan.ends_on, 1) : b.startsOn;
  if (!isDay(startsOn)) return no("Give the day the new episode starts.");
  if (startsOn <= plan.starts_on) return no("The new episode starts after this plan began.");
  return { ok: true, value: { startsOn } };
}

// ---- Stages that count as met at once -------------------------------------------------------------------

/**
 * When an episode arrives at a stage whose piece is already done, the stage counts as met at once
 * and it moves on: the RN assessment stage with the initial assessment signed early, and the care
 * plan stage with a signed plan covered by its authorizations (or the client's rule allowing it
 * without). The stage names are the company's data (the assessment types, the home_care setting).
 */
export function metAtOnce(
  stage: string | undefined,
  x: {
    assessmentStages: readonly string[];
    initialSigned: boolean;
    planStage: string;
    planReady: boolean;
  },
): boolean {
  if (!stage) return false;
  if (x.initialSigned && x.assessmentStages.includes(stage)) return true;
  return x.planReady && stage === x.planStage;
}
